<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      v-if="!inPopup"
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          <template v-if="isInquiriesView">{{ $t('ui.inquiries') }}</template>
          <template v-else-if="isCustomerOrdersView">{{ $t('ui.customerOrders') }}</template>
          <template v-else-if="isOrdersView || this.userIsWarehouseman">
            <template v-if="isIndentList">
              {{ $t('order.ordersIndent') }}
            </template>
            <template v-else>
              {{ $t('order.orders') }}
            </template>
          </template>
          <template v-else-if="isOffersView && isIndentList">{{ $t('order.indentOffers') }}</template>
          <template v-else-if="isOffersView && !isIndentList">{{ $t('order.offers') }}</template>
        </span>
        <h3 class="page-title">
          <template v-if="isCustomerOrdersView">
            {{ $t('ui.customerOrdersList') }}
          </template>
          <template v-if="$route.path === '/do-sprawdzenia' && this.userIsWarehouseman">
            {{ $t('order.toCheckInWarehouse') }}
          </template>
          <template v-if="$route.path === '/oczekujace-na-dostawe' && this.userIsWarehouseman">
            {{ $t('order.pendingProductsFromSupplier') }}
          </template>
          <template v-else>
            <template v-if="isInquiriesView">{{ $t('order.offerInquiriessList') }}</template>
            <template v-else-if="isOrdersView">
              <template v-if="isIndentList">
                {{ $t('order.ordersListIndent') }}
              </template>
              <template v-else>
                {{ $t('order.ordersList') }}
              </template>
            </template>
            <template v-else-if="isOffersView && isIndentList">{{ $t('order.offersListIndent') }}</template>
            <template v-else-if="isOffersView && !isIndentList">{{ $t('order.offersList') }}</template>
          </template>
        </h3>
      </d-col>

      <d-col
        v-if="!userIsWarehouseman && !userIsCustomer && (isOffersView || isOrdersView)"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end"
        style="gap: 0.75rem;">
        <d-checkbox
          v-if="userIsWorker"
          inline
          v-model="showAllOrders"
          toggle
          toggle-small
          :disabled="!loaded">
          {{ $t('order.showAllOrders') }}
        </d-checkbox>

        <d-button
          v-if="!userIsWarehouseman && !userIsCustomer"
          theme="primary"
          outline
          :disabled="!loaded"
          @click.prevent.stop="loadFilteredData(true)" >
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm mr-1"
            role="status">
          </div>
          <i
            v-else
            class="material-icons mr-1">
            cloud_download
          </i>
          {{ $t('order.downloadXls') }}
        </d-button>

        <d-button
          v-if="isOffersView"
          theme="success"
          @click.prevent="$router.push(isIndentList ? '/oferty-indent/nowa' : '/oferty/nowa')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewOffer') }}
        </d-button>

        <d-button
          v-if="isOrdersView"
          theme="success"
          outline
          @click.prevent="$router.push(addNewUrl)">
          <i class="material-icons">add</i>
          <template v-if="isIndentList">
            {{ $t('order.addNewOrderIndent') }}
          </template>
          <template v-else>
            {{ $t('order.addNewOrder') }}
          </template>
        </d-button>
      </d-col>

      <d-col
        v-if="userIsCustomer && isInquiriesView"
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/zapytania/nowe')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewInquiry') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      ref="listFilters"
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="loaded"
      :popup-filter="popupFilter"
      :popup-status="popupStatus"
      :view-name="'orders'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('order.customerAndName') }}</th>
            <th
              v-if="popupFilter === 'WITHOUT_SETTLEMENT'"
              scope="col"
              class="border-0"
              style="width: 180px;">{{ $t('order.problemWithInvoice') }}</th>

            <template v-if="inPopup">
              <th
                scope="col"
                class="border-0"
                style="width: 240px;">
                <template v-if="isInquiriesView">{{ $t('order.inquirySubmissionDate') }}</template>
                <template v-else-if="isOrdersView">{{ $t('order.orderSubmissionDate') }}</template>
                <template v-else>{{ $t('order.offerSubmissionDate') }}</template>
              </th>
              <th
                scope="col"
                class="border-0"
                style="width:180px;">
                {{ $t('order.sampleRequirementDate') }}
              </th>
              <th
                scope="col"
                class="border-0"
                style="width: 120px;">
                {{ $t('order.sampleRequirementDays') }}
              </th>
              <th
                scope="col"
                class="border-0"
                style="width: 180px;">{{ $t('status.status') }}</th>
              <th
                scope="col"
                class="border-0"
                style="width: 160px;"
                v-if="!userIsWarehouseman && !isInquiriesView">{{ $t('ui.value') }}</th>
            </template>
            <template v-else>
              <th
                scope="col"
                class="border-0"
                style="width: 180px;">{{ $t('status.status') }}</th>
              <th
                scope="col"
                class="border-0"
                style="width: 160px;"
                v-if="!userIsWarehouseman && !isInquiriesView">{{ $t('ui.value') }}</th>
              <th
                scope="col"
                class="border-0"
                style="width: 240px;">
                <template v-if="isInquiriesView">{{ $t('order.inquirySubmissionDate') }}</template>
                <template v-else-if="isOrdersView">{{ $t('order.orderSubmissionDate') }}</template>
                <template v-else>{{ $t('order.offerSubmissionDate') }}</template>
              </th>
            </template>

            <th
              scope="col"
              class="border-0"
              :style="actionsColumnWidth">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index"
            :class="{
              'has-shipping-warning': hasShippingDateWarning(order),
              'has-blockade-warning': hasBlockadeDateWarning(order),
              'has-unpaid-advance': hasUnpaidAdvance(order),
              'has-been-last-visited': lastViewedItem.id === order.id,
              'has-deadline': !!order.deadline && userIsAdminOrWorker
            }">
            <td class="align-middle customer-info">
              <d-badge
                v-if="order.is_priority"
                theme="warning"
                class="mb-2 mr-2">
                {{ $t('order.priorityOrder') }}
              </d-badge>
              <d-badge
                v-if="order.collect_alert"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.ordersCollectedPartially') }}
              </d-badge>
              <d-badge
                v-if="order.products.filter(product => !!product.container_id).length"
                theme="danger"
                outline
                class="mb-2 mr-2">
                {{ $t('container.container') }}
              </d-badge>
              <d-badge
                v-if="order.products.filter(product => !!product.purchase_id).length"
                theme="warning"
                outline
                class="mb-2 mr-2">
                {{ $t('order.orderFromSupplier') }}
              </d-badge>
              <d-badge
                v-if="order.blockade_status === 'BLOCKADE'"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.blockade') }}
              </d-badge>
              <d-badge
                v-if="order.blockade_status === 'RETURN_TO_STOCK'"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.returnToStock') }}
              </d-badge>
              <d-badge
                v-if="userIsWarehouseman && order.collected_again"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.collectedAgain') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_virtual"
                theme="warning"
                :outline="true"
                class="mb-2 mr-2">
                {{ $t('order.isVirtual') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_advance"
                theme="danger"
                class="mb-2 mr-2">
                {{ $t('order.badPayerRequiresAdvance') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.is_advance && !order.advance_paid"
                theme="danger"
                class="mb-2">
                {{ $t('order.advanceNotPaid') }}
              </d-badge>
              <d-badge
                v-if="userIsAdminOrWorker && order.services_received"
                theme="success"
                class="mb-2 mr-2">
                {{ $t('order.servicesReceived') }}
              </d-badge>
              <br class="br-to-hide">
              <template v-if="getPromotionalTag(order)">
                <d-badge
                  theme="secondary"
                  class="mb-1"
                  :style="'background-color: ' + getPromotionalTag(order).color + '; color: ' + contrastColor(getPromotionalTag(order).color) + ';'">
                  {{ getPromotionalTag(order).name }}
                </d-badge>
                <br>
              </template>
              {{ order.customer_name }}<br v-if="order.order_name">
              <small v-if="order.order_name">
                {{ order.order_name }}
                <template v-if="order.order_name_notes">
                  {{ order.order_name_notes }}
                </template>
              </small><br>
              <small v-if="order.deadline">
                <span v-if="!hasDeadlineWarning(order)"><strong>{{ $t('ui.deadline') }}:</strong> {{ order.deadline }}</span>
                <span
                  v-else
                  class="text-danger"><strong>{{ $t('ui.deadline') }}:</strong> <i class="material-icons">warning</i> {{ order.deadline }}</span>
              </small>
              <br v-if="order.shipper_date">
              <small v-if="order.shipper_date">
                <span v-if="!hasShippingDateWarning(order)"><strong>{{ $t('order.shipment') }}:</strong> {{ order.shipper_date }}</span>
                <span
                  v-else
                  class="text-danger"><strong>{{ $t('order.shipment') }}:</strong> <i class="material-icons">warning</i> {{ order.shipper_date }}</span>
              </small>

              <br v-if="order.blockade_status && order.blocked_at">
              <small v-if="order.blockade_status && order.blocked_at">
                <span v-if="!hasBlockadeDateWarning(order)"><strong>{{ $t('order.blockade') }}:</strong> {{ order.blocked_at.split(' ')[0] }}</span>
                <span
                  v-else
                  class="text-danger"><strong>{{ $t('order.blockade') }}:</strong> <i class="material-icons">warning</i> {{ order.blocked_at.split(' ')[0] }}</span>
              </small>

              <small v-if="!userIsCustomer && !order.order_name && order.is_inquiry">
                <span class="text-danger">
                  <i class="material-icons">warning</i>
                  {{ $t('order.orderPlacedByClientMissingInfo') }}
                </span>
              </small>

              <br v-if="!userIsCustomer && order.is_inquiry">
              <span
                v-if="!userIsCustomer && order.is_inquiry"
                class="is-inquiry-info text-primary">
                <i class="material-icons">info</i> {{ $t('order.createdFromOfferInquiry') }}
              </span>

              <br v-if="userIsWarehouseman && order.description_for_warehouse">
              <small v-if="userIsWarehouseman && order.description_for_warehouse">
                <strong>{{ $t('order.commentsForWarehouseman') }}:</strong> {{ order.description_for_warehouse }}
              </small>

              <br v-if="displayWorkerName && order.user_name">
              <small v-if="displayWorkerName && order.user_name">
                {{ $t('users.worker') }}: <span class="text-primary">{{ order.user_name }}</span>
              </small>

              <div
                v-if="order.purchases && order.purchases.length && userIsAdminOrWorker"
                class="purchase-info">
                <div
                  v-for="(purchase, index) of order.purchases"
                  :key="'order-purchase-' + order.id + '-' + index"
                  class="purchase-info-content">
                  <span>{{ $t('order.purchaseInfoStatus') }} <strong>{{ getPurchaseStatus(purchase.status) }}</strong></span>
                  <span v-if="purchase.file">{{ $t('order.purchaseInfoInvoiceFile') }}</span>
                  <span v-if="purchase.invoice_number">{{ $t('order.purchaseInfoInvoiceAdded') }} ({{ purchase.invoice_number }})</span>
                </div>
              </div>

              <d-badge
                v-if="userIsAdminOrWorker && (order.invoice_request || order.invoice_request_by_user)"
                theme="warning"
                class="mt-2">
                {{ $t('order.invoiceRequestSentText') }}
              </d-badge>

              <br v-if="userIsAdminOrWorker && order.editing_by">
              <d-badge
                v-if="userIsAdminOrWorker && order.editing_by"
                theme="warning"
                class="mb-2 mr-2">
                <i class="material-icons">lock</i>
                {{ $t('order.editInProgressBy', { user: order.edit_by.name }) }}
              </d-badge>

              <br v-if="userIsWorker && order.user_id !== getUserID">
              <span
                v-if="userIsWorker && order.user_id !== getUserID"
                class="text-danger mt-2 d-block">
                {{ $t('order.orderAssignedTo') }}: <strong>{{ order.user_name }}</strong>
              </span>

              <orders-list-boxes-status
                v-if="userIsWarehouseman && order.boxes"
                :boxes="order.boxes"
                :boxesCount="order.boxes_count"
                :boxesCollected="order.boxes_collected"
                :boxesCollectedBy="order.boxes_collected_by"
                :boxesCollectedCount="order.boxes_collected_count"
                :boxesCollectedToday="order.boxes_collected_today"
                :boxesReturned="order.boxes_returned"
                :boxesReturnedBy="order.boxes_returned_by"
                :boxesReturnedCount="order.boxes_returned_count"
                :boxesReturnedToday="order.boxes_returned_today"
                :boxesVerificationStatus="order.boxes_verification_status"
                :boxesVerified="order.boxes_verified"
                :boxesVerifiedBy="order.boxes_verified_by"
                :boxesVerifiedCount="order.boxes_verified_count"
                :boxesVerifiedToday="order.boxes_verified_today"
                :differentInProducts="order.different_in_products" />
            </td>

            <td
              v-if="popupFilter === 'WITHOUT_SETTLEMENT'"
              class="align-middle">
              <d-badge
                v-if="order.lack_invoice_reason === 'INVOICE'"
                theme="danger"
                outline>
                {{ $t('order.noInvoice') }}
              </d-badge>
              <d-badge
                v-if="order.lack_invoice_reason === 'FILE'"
                theme="danger"
                outline>
                {{ $t('order.noFile') }}
              </d-badge>
            </td>

            <template v-if="inPopup">
              <td class="align-middle">
                <template v-if="isOrdersView">
                  {{ order.order_created_at }}<br>
                </template>
                <template v-else>
                  {{ order.created_at }}<br>
                </template>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>

              <td class="align-middle text-center">
                {{ order.sample_requirement_date }}
              </td>

              <td class="align-middle text-center">
                {{ order.sample_requirement_days }}
              </td>

              <td class="align-middle">
                <d-badge
                  :class="userIsAdminOrWorker ? 'cursor-pointer' : null"
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)"
                  @click.native.prevent.stop="userIsAdminOrWorker ? showOrderStatusPopup(order.order_name, order.id, order.status) : false">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge><br>

                <d-badge
                  v-if="!order.is_virtual && !order.exclude_from_erp_integration"
                  :theme="order.erp_optima_updated_at ? 'success' : 'danger'"
                  class="w-100 mt-1"
                  :outline="true">
                  <template v-if="order.erp_optima_updated_at">
                    {{ $t('order.erp.integratedWithErp') }}
                  </template>
                  <template v-else>
                    {{ $t('order.erp.notIntegratedWithErp') }}
                  </template>
                </d-badge><br>

                <template v-if="order.is_special_requirement">
                  <d-badge
                    :id="'special-requirement-' + order.id"
                    theme="warning"
                    :outline="true"
                    style="cursor: help;">
                    {{ $t('order.hasSpecialRequirement') }}
                  </d-badge>
                  <d-tooltip
                    :target="'special-requirement-' + order.id"
                    :container="'special-requirement-wrapper-' + order.id">
                    {{ order.special_requirement }}
                  </d-tooltip>
                  <br>
                </template>

                <small
                  v-if="order.collect_warning"
                  class="text-danger">
                  <i class="material-icons">report_problem</i> {{ $t('order.foundDifferencesInCollectedProductsQuantity') }}<br>
                </small>

                <template v-if="!isAllWarehouseOrdersView && userIsWarehouseman && order.status === 'TO_COLLECT' && order.collect_user_id === 0">
                  <d-button
                    theme="success"
                    :disabled="!!takeOrderInProgress"
                    class="ml-auto mt-2 mb-1"
                    outline
                    size="sm"
                    @click.prevent="takeOrder(order.id)">
                    <template v-if="!takeOrderInProgress || takeOrderInProgress !== order.id">
                      {{ $t('order.iAcceptOrder') }}
                    </template>
                    <template v-if="takeOrderInProgress === order.id">
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </d-button>
                  <br>
                </template>

                <small v-if="order.products && !isInquiriesView">
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small v-if="!hasServicesWarnings(order) && !isInquiriesView">
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
                <small
                  v-else-if="!isInquiriesView"
                  class="text-danger">
                  {{ $t('order.servicesGenitive') }}: <i class="material-icons">warning</i>
                  <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>

                <template v-if="order.synchronised_at && order.softlab_blockade_status">
                  <br>
                  <small>
                    {{ $t('order.softlabBlockadeStatus') }}:
                    <span
                      v-if="order.softlab_blockade_status === 'FULL'"
                      class="text-success">
                      {{ $t('order.softlabBlockadeStatusFull') }}
                    </span>
                    <span
                      v-if="order.softlab_blockade_status === 'PARTIAL'"
                      class="text-warning">
                      {{ $t('order.softlabBlockadeStatusPartial') }}
                    </span>
                  </small>
                </template>
              </td>

              <td
                v-if="!userIsWarehouseman && !isInquiriesView"
                class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
            </template>

            <template v-else>
              <td
                :id="'special-requirement-wrapper-' + order.id"
                class="align-middle">
                <d-badge
                  v-if="lastViewedItem.id === order.id"
                  theme="warning"
                  class="w-100 text-center mb-1">
                  <template v-if="lastViewedItem.action === 'viewed'">
                    {{ $t('order.lastItemViewed') }}
                  </template>
                  <template v-else>
                    {{ $t('order.lastItemModified') }}
                  </template>
                </d-badge>

                <d-badge
                  :class="userIsAdminOrWorker ? 'cursor-pointer' : null"
                  :theme="getStatusClass(order.status)"
                  :outline="getOutline(order.status, order.collect_user_id)"
                  class="w-100"
                  @click.native.prevent.stop="userIsAdminOrWorker ? showOrderStatusPopup(order.order_name, order.id, order.status) : false">
                  {{ getStatusName(order.status, order.collect_user_id) }}
                </d-badge>
                <br>

                <d-badge
                  v-if="!order.is_virtual && !order.exclude_from_erp_integration"
                  :theme="order.erp_optima_updated_at ? 'success' : 'danger'"
                  class="w-100 mt-1"
                  :outline="true">
                  <template v-if="order.erp_optima_updated_at">
                    {{ $t('order.erp.integratedWithErp') }}
                  </template>
                  <template v-else>
                    {{ $t('order.erp.notIntegratedWithErp') }}
                  </template>
                </d-badge>

                <small
                  v-if="order.exclude_from_erp_integration"
                  class="text-danger d-block mt-2"
                  style="font-size:70%;">
                  {{ $t('order.erp.isExcluded') }}
                </small>

                <br v-if="!order.is_virtual && !order.exclude_from_erp_integration">

                <small v-if="order.latest_erp_optima_query && !order.exclude_from_erp_integration">
                  {{ $t('order.erp.latestQuery') }}:
                  <span
                    :class="{
                      'text-success': order.latest_erp_optima_query.status === 'DONE',
                      'text-warning': order.latest_erp_optima_query.status === 'WAITING',
                      'text-danger': order.latest_erp_optima_query.status === 'ERROR',
                      'text-primary': order.latest_erp_optima_query.status === 'QUEUED'
                    }"
                    :title="order.latest_erp_optima_query.status === 'ERROR' ? order.latest_erp_optima_query.error_message : null">
                    {{ $t('order.erp.latestQueryStatus.' + order.latest_erp_optima_query.status) }}
                  </span>
                </small>

                <br v-if="order.latest_erp_optima_query">

                <template v-if="order.is_special_requirement">
                  <d-badge
                    :id="'special-requirement-' + order.id"
                    theme="warning"
                    :outline="true"
                    style="cursor: help;">
                    {{ $t('order.hasSpecialRequirement') }}
                  </d-badge>
                  <d-tooltip
                    :target="'special-requirement-' + order.id"
                    :container="'special-requirement-wrapper-' + order.id">
                    {{ order.special_requirement }}
                  </d-tooltip>
                  <br>
                </template>

                <small
                  v-if="order.collect_warning"
                  class="text-danger">
                  <i class="material-icons">report_problem</i> {{ $t('order.foundDifferencesInCollectedProductsQuantity') }}<br>
                </small>

                <template v-if="!isAllWarehouseOrdersView && userIsWarehouseman && order.status === 'TO_COLLECT' && order.collect_user_id === 0">
                  <d-button
                    theme="success"
                    :disabled="!!takeOrderInProgress"
                    class="ml-auto mt-2 mb-1"
                    outline
                    size="sm"
                    @click.prevent="takeOrder(order.id)">
                    <template v-if="!takeOrderInProgress || takeOrderInProgress !== order.id">
                      {{ $t('order.iAcceptOrder') }}
                    </template>
                    <template v-if="takeOrderInProgress === order.id">
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </d-button>
                  <br>
                </template>

                <template v-if="complaintsStatus(order.complaints)">
                  <d-badge
                    v-if="complaintsStatus(order.complaints) === 'IN_REALIZATION'"
                    class="mt-2 mb-1"
                    theme="danger">
                    {{ $t('complaints.complaint') }}
                  </d-badge>
                  <d-badge
                    v-else
                    class="mt-2 mb-1"
                    theme="success">
                    {{ $t('complaints.complaint') }}
                  </d-badge>
                  <br>
                </template>

                <small v-if="order.products && !isInquiriesView">
                  {{ $t('ui.productsGenitive') }}: <strong>{{ order.products.length }}</strong>
                </small>
                <br>
                <small v-if="!hasServicesWarnings(order) && !isInquiriesView">
                  {{ $t('order.servicesGenitive') }}: <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>
                <small
                  v-else-if="!isInquiriesView"
                  class="text-danger">
                  {{ $t('order.servicesGenitive') }}: <i class="material-icons">warning</i> <strong>{{ getServicesCount(order.products, order.services) }}</strong>
                </small>

                <template v-if="order.synchronised_at && order.softlab_blockade_status">
                  <br>
                  <small>
                    {{ $t('order.softlabBlockadeStatus') }}:
                    <span
                      v-if="order.softlab_blockade_status === 'FULL'"
                      class="text-success">
                      {{ $t('order.softlabBlockadeStatusFull') }}
                    </span>
                    <span
                      v-if="order.softlab_blockade_status === 'PARTIAL'"
                      class="text-warning">
                      {{ $t('order.softlabBlockadeStatusPartial') }}
                    </span>
                  </small>
                </template>
              </td>
              <td
                v-if="!userIsWarehouseman && !isInquiriesView"
                class="align-middle">
                {{ Number(order.price).toFixed(2) }} {{ getCurrency(order.currency_id) }}<br>
                <small>
                  {{ $t('order.invoicesIssued') }}: <strong>{{ order.settlements.length }}</strong>
                </small>
              </td>
              <td class="align-middle">
                <template v-if="isOrdersView">
                  {{ order.order_created_at }}<br>
                </template>
                <template v-else>
                  {{ order.created_at }}<br>
                </template>
                <small>({{ $t('order.lastChange') }} {{ order.updated_at }})</small>
              </td>
            </template>

            <td class="align-middle">
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="primary"
                outline
                @click.native="openOrderSummaryPopup(order.id)"
                class="mr-2"
                :title="$t('order.orderPreview')">
                <i class="material-icons">search</i>
              </d-button>
              <d-button
                v-if="!(inPopup && forcedCustomerID)"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.is_indent, false)"
                class="mr-2"
                :title="!userIsWarehouseman && !userIsCustomer ? $t('ui.edit') : $t('order.see')">
                <i
                  class="material-icons"
                  v-if="!userIsWarehouseman && !userIsCustomer">edit</i>
                <i
                  class="material-icons"
                  v-if="userIsWarehouseman || userIsCustomer">visibility</i>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="primary"
                @click.native="orderActionUrl(order.id, order.is_indent, true)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">open_in_new</i>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer && !isCustomerOrdersView"
                theme="secondary"
                @click.native="duplicateOrder(order.id)"
                :disabled="duplicateInProgress || removeInProgress"
                class="mr-2"
                :title="$t('ui.duplicate')">
                <i class="material-icons">file_copy</i>
              </d-button>
              <d-dropdown
                v-if="!userIsWarehouseman && !userIsCustomer && !isCustomerOrdersView"
                icon="cloud_download"
                theme="info"
                class="mr-2">
                <d-dropdown-item>
                  <a
                    :disabled="downloadingPdf !== false"
                    @click.prevent="downloadPdf(order.id, order.order_name)"
                    :style="'display: block;' + ((downloadingPdf !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingPdf !== order.id">{{ $t('ui.pdf') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item>
                  <a
                    :disabled="downloadingXls !== false"
                    @click.prevent="downloadXls(order.id, order.status)"
                    :style="'display: block;' + ((downloadingXls !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingXls !== order.id">{{ $t('ui.xls') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item>
                  <a
                    :disabled="downloadingSummary !== false"
                    @click.prevent="downloadSummary(order.id, order.order_name)"
                    :style="'display: block;' + ((downloadingSummary !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingSummary !== order.id">{{ $t('order.summary') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item v-if="order.settlements.length > 0">
                  <a
                    :disabled="downloadingInvoice !== false"
                    @click.prevent="downloadInvoice(order)"
                    :style="'display: block;' + ((downloadingInvoice !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingInvoice !== order.id && order.settlements.length === 1">{{ $t('order.invoicePdf') }}</template>
                    <template v-else-if="downloadingInvoice !== order.id && order.settlements.length > 1">{{ $t('order.invoiceZip') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
              </d-dropdown>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadMarkingDoc(order.id)"
                :disabled="!!downloadingMarkingInProgress"
                theme="primary"
                class="mr-2"
                :title="$t('order.downloadOrderMarkingDoc')">
                <template v-if="downloadingMarkingInProgress !== order.id">
                  <i class="material-icons">sim_card_download</i>
                </template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadSummary(order.id, order.order_name)"
                :disabled="!!downloadingSummary"
                theme="info"
                :title="$t('order.downloadOrderSummary')">
                <template v-if="downloadingSummary !== order.id">
                  <i class="material-icons">cloud_download</i>
                </template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="userIsWarehouseman"
                @click="downloadAttachment(order.id)"
                :disabled="!!downloadingAttachment || !order.attachment"
                theme="warning"
                class="ml-2"
                :title="$t('order.downloadOrderAttachment')">
                <template v-if="downloadingAttachment !== order.id">
                  <i class="material-icons">description</i>
                </template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>
              <d-button
                v-if="!userIsWarehouseman && !userIsCustomer"
                theme="danger"
                :disabled="!order.can_delete || removeInProgress || duplicateInProgress"
                @click.native="removeOrder(order.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>

              <br>

              <d-button
                v-if="popupFilter === 'OBTAINING' && order.can_send_waiting_sample"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_SAMPLE')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'OBTAINING' && order.waiting_sample_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_sample_date }}
              </small>

              <d-button
                v-if="popupFilter === 'ACCEPTING' && order.can_send_waiting_accept"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_ACCEPT')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'ACCEPTING' && order.waiting_accept_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_accept_date }}
              </small>

              <d-button
                v-if="popupFilter === 'ENDING_PRODUCTION' && order.can_send_waiting_production"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_PRODUCTION')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'ENDING_PRODUCTION' && order.waiting_production_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_production_date }}
              </small>

              <d-button
                v-if="popupFilter === 'DRAFT' && order.can_send_waiting_draft"
                theme="secondary"
                :outline="true"
                class="w-100 mt-2 text-center"
                @click.native="sendReminder(order.id, 'WAITING_DRAFT')"
                :disabled="!!reminderSendingInProgress">
                <template v-if="reminderSendingInProgress !== order.id">
                  <i class="material-icons">mail</i>
                  {{ $t('order.sendReminder') }}
                </template>
                <div
                  v-if="reminderSendingInProgress === order.id"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
              </d-button>

              <small v-if="popupFilter === 'DRAFT' && order.waiting_draft_date">
                {{ $t('order.lastSendReminderDate') }}: {{ order.waiting_draft_date }}
              </small>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="8"
              class="text-center">
              <template v-if="isInquiriesView">{{ $t('order.noOfferInquriesToDisplay') }}&hellip;</template>
              <template v-else>{{ $t('order.noOffersOrOrdersToDisplay') }}&hellip;</template>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="displayChangeStatusPopup"
      @close="displayChangeStatusPopup = false">
      <d-modal-header>
        <d-modal-title>
          {{ $t('order.changeOrderStatus') }}: {{ statusChangeOrderName }}
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <d-form>
          <div class="form-group">
            <label for="name">
              {{ $t('order.selectNewStatus') }}:
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="orderStatusOptions"
                v-model="statusChangeOrderStatus"
                :searchable="false"
                :clearable="false"
                :disabled="statusChangeInProgress"
                :reduce="status => status.value"
                label="text">
              </v-select>
            </div>

            <d-alert
              :show="statusChangeOrderStatus === 'TO_DELETE' || statusChangeOrderStatus === 'DELETED'"
              class="text-center mt-4 mb-0"
              theme="danger">
              {{ $t('order.deletedStatusCannotBeChanged') }}
            </d-alert>

            <d-alert
              :show="statusChangeOrderStatus === 'TO_DELETE' || statusChangeOrderStatus === 'DELETED'"
              class="text-center mt-4 mb-0"
              theme="danger">
              {{ $t('order.reservationsInContainersAndSuppliersCancelledAfterStatusChange') }}
            </d-alert>
          </div>
        </d-form>
      </d-modal-body>
      <d-modal-footer>
        <d-button
          class="ml-0"
          theme="danger"
          :disabled="statusChangeInProgress"
          @click.prevent="displayChangeStatusPopup = false">
          {{ $t('ui.cancel') }}
        </d-button>
        <d-button
          class="ml-auto"
          theme="success"
          :disabled="statusChangeInProgress"
          @click.prevent="saveNewOrderStatus()">
          <div
            v-if="statusChangeInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          {{ $t('ui.save') }}
        </d-button>
      </d-modal-footer>
    </d-modal>

    <order-summary-popup
      v-if="showOrderSummaryPopup"
      :orderID="orderSummaryID" />
  </d-container>
</template>

<script>
import Vue from 'vue';
import HasPagination from '@/mixins/HasPagination.vue';
import CustomerPanel from '@/mixins/CustomerPanel.vue';
import CommonGetters from '@/utils/CommonGetters.js';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import orderStatusClasses from '@/data/order-status-classes.js';
import orderStatusPhase from '@/data/order-status-phase.js';
import orderStatuses from '@/data/order-statuses.js';
import Pagination from '@/components/common/Pagination.vue';
import OrderSummaryPopup from '@/components/orders/OrderSummaryPopup.vue';
import OrdersListBoxesStatus from '@/components/orders/OrdersListBoxesStatus.vue';
import { mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'orders',
  mixins: [
    CustomerPanel,
    HasPagination
  ],
  props: {
    customerPopupContent: {
      default: false
    },
    dashboardParam: {
      default: false
    },
    forcedCustomerID: {
      default: false
    },
    inPopup: {
      default: false
    },
    popupFilter: {
      default: ''
    },
    popupShowAll: {
      default: false
    },
    popupShowAllUI: {
      default: false
    },
    popupStatus: {
      default: ''
    },
    showWorkers: {
      default: false
    }
  },
  components: {
    'order-summary-popup': OrderSummaryPopup,
    'orders-list-boxes-status': OrdersListBoxesStatus,
    'pagination': Pagination
  },
  watch: {
    showAllOrders: {
      handler () {
        if (this.allDataLoaded) {
          this.debouncedLoadFilteredData();
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'getUserID',
      'getUserType'
    ]),
    addNewUrl () {
      if (this.isIndentList) {
        return '/zamowienia-indent/nowe/';
      }

      return '/zamowienia/nowe/';
    },
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies && this.loadedWorkers && this.loadedContractors && this.loadedProducts && this.loadedCustomerTags && this.loadedOrderTags && this.loadedPromotionalTags && this.loadedWarehousemans;
    },
    userIsAdmin () {
      return this.getUserType === 'admin';
    },
    userIsAdminOrWorker () {
      return this.getUserType === 'admin' || this.getUserType === 'worker';
    },
    userIsWorker () {
      return this.getUserType === 'worker';
    },
    userIsWarehouseman () {
      return this.getUserType === 'warehouse_manager';
    },
    displayWorkerName () {
      return this.showWorkers || (this.inPopup && this.userIsWorker && this.activeFilters.show_all);
    },
    isCustomerOrdersView () {
      return this.$route.path === '/zamowienia-partnerow';
    },
    isInquiriesView () {
      return this.$route.path === '/zapytania';
    },
    isOrdersView () {
      return this.$route.path === '/zamowienia' || this.$route.path === '/zamowienia-indent' || (this.inPopup && this.customerPopupContent === 'orders');
    },
    isAllWarehouseOrdersView () {
      return this.$route.path === '/zamowienia-wszystkie';
    },
    isOffersView () {
      return this.$route.path === '/oferty' || this.$route.path === '/oferty-indent' || (this.inPopup && this.customerPopupContent === 'offers');
    },
    isIndentList () {
      return this.$route.path.indexOf('/zamowienia-indent') > -1 || this.$route.path.indexOf('/oferty-indent') > -1;
    },
    isOrdersList () {
      return this.$route.path === '/zamowienia';
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    hasSaleProductsOptions () {
      return [
        {
          text: this.$t('order.hasSaleProducts'),
          value: 1
        },
        {
          text: this.$t('order.hasNoSaleProducts'),
          value: 0
        }
      ];
    },
    orderIECommunicateOptions () {
      return [
        {
          text: this.$t('order.hasIECommunicate'),
          value: 1
        },
        {
          text: this.$t('order.hasNotIECommunicate'),
          value: 0
        }
      ];
    },
    orderPriorityOptions () {
      return [
        {
          text: this.$t('order.priority'),
          value: 1
        },
        {
          text: this.$t('order.noPriority'),
          value: 0
        }
      ];
    },
    orderRepeatedOptions () {
      return [
        {
          text: this.$t('order.repeated'),
          value: 1
        },
        {
          text: this.$t('order.notRepeated'),
          value: 0
        }
      ];
    },
    orderChangedOptions () {
      return [
        {
          text: this.$t('order.changed'),
          value: 1
        },
        {
          text: this.$t('order.notChanged'),
          value: 0
        }
      ];
    },
    orderExclamationOptions () {
      return [
        {
          text: this.$t('order.delayed'),
          value: 1
        }
      ];
    },
    orderBlockadeOptions () {
      return [
        {
          text: this.$t('order.blocked'),
          value: 'BLOCKADE'
        },
        {
          text: this.$t('order.returnToStock'),
          value: 'RETURN_TO_STOCK'
        }
      ];
    },
    orderSLBlockadeOptions () {
      return [
        {
          text: this.$t('order.softlabBlockadeStatus') + ': ' + this.$t('order.softlabBlockadeStatusFull'),
          value: 'FULL'
        },
        {
          text: this.$t('order.softlabBlockadeStatus') + ': ' + this.$t('order.softlabBlockadeStatusPartial'),
          value: 'PARTIAL'
        }
      ];
    },
    orderNewOrderOptions () {
      return [
        {
          text: this.$t('order.warehouseNewOrders'),
          value: 1
        },
        {
          text: this.$t('order.warehouseNotNewOrders'),
          value: 0
        }
      ];
    },
    orderSpecialRequirementOptions () {
      return [
        {
          text: this.$t('order.hasSpecialRequirement'),
          value: 1
        },
        {
          text: this.$t('order.hasNoSpecialRequirement'),
          value: 0
        }
      ];
    },
    orderRepeatedOrderOptions () {
      return [
        {
          text: this.$t('order.warehouseRepeatedOrders'),
          value: 1
        },
        {
          text: this.$t('order.warehouseNotRepeatedOrders'),
          value: 0
        }
      ];
    },
    orderDelayedSampleOptions () {
      return [
        {
          text: this.$t('order.delayedSamples'),
          value: 1
        },
        {
          text: this.$t('order.notDelayedSamples'),
          value: 0
        }
      ];
    },
    invoiceRequestStatusOptions () {
      return [
        {
          text: this.$t('order.optionInvoiceRequestSent'),
          value: 1
        },
        {
          text: this.$t('order.optionInvoiceRequestNotSent'),
          value: 0
        }
      ];
    },
    orderStatusOptions () {
      if (this.isOffersView) {
        return [
          {
            text: this.$t('order.newOffer'),
            value: 'NEW'
          },
          {
            text: this.$t('order.sentToCustomer'),
            value: 'SENT_TO_CUSTOMER'
          },
          {
            text: this.$t('order.offerClosed'),
            value: 'OFFER_CLOSED'
          }
        ];
      }

      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    servicesReceivedOptions () {
      return [
        {
          text: this.$t('order.servicesReceived'),
          value: 1
        },
        {
          text: this.$t('order.servicesNotReceived'),
          value: 0
        }
      ];
    },
    customerTagsOptions () {
      return this.customerTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    orderTagsOptions () {
      return this.orderTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    promotionalTagsOptions () {
      return this.promotionalTags.map(tag => ({
        text: tag.name,
        value: tag.id
      }));
    },
    productTagsOptions () {
      let options = [];

      for (let i = 0; i < this.productTags.length; i++) {
        for (let j = 0; j < this.productTags[i].tags.length; j++) {
          let tagName = this.productTags[i].tags[j].name;
          let tagID = this.productTags[i].tags[j].id;
          let tagCategoryID = this.productTags[i].tags[j].category_id;
          let tagCategoryName = this.productTags[i].name;

          let option = {
            text: tagCategoryName + ': ' + tagName,
            value: tagCategoryID + ';' + tagID
          };

          options.push(option);
        }
      }

      return options;
    },
    preloadedProductCodesOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: (this.products[i].parent_id ? '🟩 ' : '⬜️ ') + this.products[i].code + ' - ' + this.products[i].name,
          value: '[#' + this.products[i].id + '#]' + this.products[i].code,
          valueLowerCase: ('[#' + this.products[i].id + '#]' + this.products[i].code).toLowerCase()
        };

        options.push(option);
      }

      return options;
    },
    productCodesOptions () {
      if (this.$refs['product-codes-filter'] && this.$refs['product-codes-filter'].search.length < 1) {
        return [];
      }

      return this.preloadedProductCodesOptions;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    paidStatusOptions () {
      return [
        {
          text: this.$t('ui.settled'),
          value: 'PAID'
        },
        {
          text: this.$t('ui.notSettled'),
          value: 'UNPAID'
        }
      ];
    },
    erpStatusOptions () {
      return [
        {
          text: this.$t('order.erp.integratedWithErp'),
          value: 1
        },
        {
          text: this.$t('order.erp.notIntegratedWithErp'),
          value: 0
        },
        {
          text: this.$t('order.erp.isExcluded'),
          value: -1
        }
      ];
    },
    actionsColumnWidth () {
      if (this.userIsWarehouseman) {
        return 'width: 260px;';
      }

      if (this.userIsCustomer) {
        return 'width: 80px;';
      }

      if (this.isCustomerOrdersView) {
        return 'width: 250px;';
      }

      return 'width: 360px;';
    },
    subviewName () {
      if (this.isInquiriesView) {
        return 'inquiries';
      }

      if (this.isOffersView) {
        return 'offers';
      }

      return false;
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.newOrder'),
          value: 'NEW'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.closed'),
          value: 'CLOSED'
        }
      ];
    },
    filtersConfig () {
      return [
        {
          key: 'show_all',
          label: this.$t('order.showAllOrders'),
          type: 'checkbox',
          isBasic: true,
          defaultValue: this.inPopup ? !!this.popupShowAll : false,
          permission: () => this.userIsWorker && this.inPopup && this.popupShowAllUI,
          apiTransform: () => {}
        },
        {
          key: 'order_name',
          label: this.isInquiriesView ? this.$t('order.inquiryName') : this.$t('ui.orderName'),
          placeholder: this.isInquiriesView ? this.$t('order.inquiryName') : this.$t('ui.orderName'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'status',
          apiKey: 'status',
          label: this.$t('status.statusQuestionMark'),
          placeholder: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.orderStatusOptions
        },
        {
          key: 'settlement_number',
          apiKey: 'settlement_number',
          label: this.$t('order.settlementNumber'),
          placeholder: this.$t('order.settlementNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'tracking_number',
          apiKey: 'tracking_number',
          label: this.$t('order.trackingNumber'),
          placeholder: this.$t('order.trackingNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'customer_id',
          apiKey: 'customer_id',
          label: this.$t('order.filters.client'),
          type: 'customerSelect',
          isBasic: true,
          defaultValue: null,
          permission: () => !this.userIsWarehouseman && !this.userIsCustomer && !(this.inPopup && this.forcedCustomerID)
        },
        {
          key: 'created_at_range',
          label: this.$t('order.filters.orderDate'),
          type: 'daterange',
          isBasic: false,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['created_at_from'] = CalendarUtils.formatDateForPHP(new Date(val.startDate));
            }
            if (val.endDate) {
              where['created_at_to'] = CalendarUtils.formatDateForPHP(new Date(val.endDate));
            }
          }
        },
        {
          key: 'is_repeated',
          apiKey: 'is_repeated',
          label: this.$t('order.isRepeated'),
          placeholder: this.$t('order.isRepeated'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderRepeatedOptions
        },
        {
          key: 'orderTags',
          apiKey: 'tags',
          label: this.$t('order.orderTags'),
          placeholder: this.$t('order.orderTags'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.orderTagsOptions
        },
        {
          key: 'promotionalTag',
          apiKey: 'promotional_tag_id',
          label: this.$t('settings.promotionalTags.promotionalTag'),
          placeholder: this.$t('settings.promotionalTags.promotionalTag'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.promotionalTagsOptions
        },
        {
          key: 'ie_communicate',
          apiKey: 'ie_communicate',
          label: this.$t('order.ieCommunicate'),
          placeholder: this.$t('order.ieCommunicate'),
          type: 'select',
          isBasic: false,
          permission: ['admin', 'worker'],
          defaultValue: null,
          options: this.orderIECommunicateOptions
        },
        {
          key: 'services_received',
          apiKey: 'services_received',
          label: this.$t('order.servicesReceived') + '?',
          placeholder: this.$t('order.servicesReceived') + '?',
          type: 'select',
          isBasic: false,
          permission: ['admin', 'worker'],
          defaultValue: null,
          options: this.servicesReceivedOptions
        },
        {
          key: 'country_id',
          apiKey: 'country_id',
          label: this.$t('customer.customerCountryQuestionMark'),
          placeholder: this.$t('customer.customerCountryQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.countriesOptions
        },
        {
          key: 'customerTags',
          apiKey: 'customer_tags',
          label: this.$t('order.customerTags'),
          placeholder: this.$t('order.customerTags'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.customerTagsOptions
        },
        {
          key: 'purchase_status',
          apiKey: 'purchase_status',
          label: this.$t('status.purchaseStatusQuestionMark'),
          placeholder: this.$t('status.purchaseStatusQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.purchaseStatusOptions,
          description: this.$t('order.filters.purchaseStatusDesc')
        },
        {
          key: 'is_priority',
          apiKey: 'is_priority',
          label: this.$t('order.isPriority'),
          placeholder: this.$t('order.isPriority'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderPriorityOptions
        },
        {
          key: 'exclamation',
          apiKey: 'exclamation',
          label: this.$t('order.isDelayed'),
          placeholder: this.$t('order.isDelayed'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderExclamationOptions
        },
        {
          key: 'blockade',
          apiKey: 'blockade_status',
          label: this.$t('order.blockedQuestionMark'),
          placeholder: this.$t('order.blockedQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderBlockadeOptions,
          description: this.$t('order.filters.blockadeDesc')
        },
        {
          key: 'softlab_blockade_status',
          apiKey: 'softlab_blockade_status',
          label: this.$t('order.softlabBlockadeStatus') + '?',
          placeholder: this.$t('order.softlabBlockadeStatus') + '?',
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderSLBlockadeOptions
        },
        {
          key: 'user_id',
          apiKey: 'user_id',
          label: this.$t('order.isWorker'),
          placeholder: this.$t('order.isWorker'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderWorkerOptions
        },
        {
          key: 'contractor_id',
          apiKey: 'contractor_id',
          label: this.$t('order.isContractor'),
          placeholder: this.$t('order.isContractor'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.contractors.map(c => ({ text: c.name, value: c.id }))
        },
        {
          key: 'settlement',
          apiKey: 'settlement-status',
          label: this.$t('ui.isSettled'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.paidStatusOptions
        },
        {
          key: 'is_erp_synchronised',
          apiKey: 'is_erp_synchronised',
          label: this.$t('order.erp.isErpSynchronised'),
          placeholder: this.$t('order.erp.isErpSynchronised'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.erpStatusOptions
        },
        {
          key: 'is_special_requirement',
          apiKey: 'is_special_requirement',
          label: this.$t('order.hasSpecialRequirementMark'),
          placeholder: this.$t('order.hasSpecialRequirementMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderSpecialRequirementOptions,
          description: this.$t('order.filters.specialReqDesc')
        },
        {
          key: 'invoice_request',
          apiKey: 'invoice_request',
          label: this.$t('order.invoiceRequestStatusQuestionMark'),
          placeholder: this.$t('order.invoiceRequestStatusQuestionMark'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.invoiceRequestStatusOptions
        },
        {
          key: 'has_sale_products',
          apiKey: 'has_sale_products',
          label: this.$t('order.hasSaleProductsQuestion'),
          placeholder: this.$t('order.hasSaleProductsQuestion'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.hasSaleProductsOptions
        },
        {
          key: 'sampleRequirementOverdue',
          apiKey: 'sample_requirement_overdue',
          label: this.$t('order.isDelayedSample'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.orderDelayedSampleOptions,
          permission: () => this.inPopup && !this.isInquiriesView && !this.isCustomerOrdersView
        },

        // ============ ZAAWANSOWANE TRANSFORMACJE PRODUKTÓW ============
        {
          key: 'productCode',
          label: this.$t('product.productsCodes'),
          placeholder: this.$t('product.productsCodes'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.preloadedProductCodesOptions,
          description: this.$t('order.filters.productCodeDesc'),
          filterMethod: this.filteredProductCodes,
          apiTransform: (val, where) => {
            if (!where['products']) {
              where.products = {};
            }
            where['products']['code'] = val.map(v => v.replace(/\[\#[0-9]{1,}\#\]/gmi, ''));
          }
        },
        {
          key: 'productTags',
          label: this.$t('order.productsTags'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.productTagsOptions,
          apiTransform: (val, where) => {
            if (!where['products']) {
              where.products = {};
            }
            let output = {};
            val.forEach(tagDataStr => {
              let tagData = tagDataStr.split(';');
              let catId = parseInt(tagData[0], 10);
              let tagId = parseInt(tagData[1], 10);
              if (!output[catId]) {
                output[catId] = [];
              }
              output[catId].push(tagId);
            });
            where['products']['tags'] = output;
          }
        },
        {
          key: 'warehouse_panel_new_orders',
          apiKey: 'warehouse_panel_new_orders',
          label: this.$t('order.newOrdersQuestionMark'),
          placeholder: this.$t('order.newOrdersQuestionMark'),
          type: 'select',
          isBasic: false,
          permission: 'warehouse_manager',
          defaultValue: null,
          options: this.orderNewOrderOptions
        },
        {
          key: 'warehouse_panel_is_repeated',
          apiKey: 'warehouse_panel_is_repeated',
          label: this.$t('order.repeatedOrdersQuestionMark'),
          placeholder: this.$t('order.repeatedOrdersQuestionMark'),
          type: 'select',
          isBasic: false,
          permission: 'warehouse_manager',
          defaultValue: null,
          options: this.orderRepeatedOrderOptions
        },
        {
          key: 'warehouse_panel_updated_orders',
          apiKey: 'warehouse_panel_updated_orders',
          label: this.$t('order.orderChangedStatus') + '?',
          placeholder: this.$t('order.orderChangedStatus') + '?',
          type: 'select',
          isBasic: false,
          permission: 'warehouse_manager',
          defaultValue: null,
          options: this.orderChangedOptions
        },
        {
          key: 'collected_by_user_id',
          apiKey: 'collected_by_user_id',
          label: this.$t('order.selectWarehouseman'),
          placeholder: this.$t('order.selectWarehouseman'),
          type: 'select',
          isBasic: false,
          permission: 'warehouse_manager',
          defaultValue: null,
          options: this.warehousemans.map(w => ({ text: w.name, value: w.id }))
        }
      ];
    },
    baseApiWhere () {
      let base = {};

      if (this.isIndentList) {
        base.is_indent = 1;
      }
      if (this.isOrdersList) {
        base.is_indent = 0;
      }
      if (this.dashboardParam) {
        base.dashboard = 1;
      }

      if (this.inPopup) {
        base.order_by_is_priority = 1;
        base.exclamation = '';
        if (this.forcedCustomerID) {
          base.customer_id = this.forcedCustomerID;
        }
      }

      const currentPath = this.$route.path.replace(/\/$/, '');

      if (currentPath === '/zamowienia' || currentPath === '/zamowienia-indent' || (this.inPopup && this.customerPopupContent === 'orders')) {
        if (this.userIsWarehouseman) {
          base.status = ['TO_COLLECT', 'TO_DELETE', 'DELETED'];
        } else {
          base.excluded_status = ['INQUIRY', 'NEW', 'OFFER_CLOSED', 'SENT_TO_CUSTOMER', 'DRAFT', 'CUSTOMER_ORDER'];
        }
      } else if (currentPath === '/zamowienia-partnerow') {
        base.status = ['CUSTOMER_ORDER'];
      } else if (currentPath === '/oferty' || currentPath === '/oferty-indent' || (this.inPopup && this.customerPopupContent === 'offers')) {
        base.status = ['NEW', 'SENT_TO_CUSTOMER', 'OFFER_CLOSED'];
      } else if (currentPath === '/do-sprawdzenia') {
        base.status = 'TO_CHECK';
      } else if (currentPath === '/zapytania') {
        base.status = this.userIsCustomer ? ['INQUIRY', 'SENT_TO_CUSTOMER'] : 'INQUIRY';
      } else if (currentPath === '/oczekujace-na-dostawe' && this.userIsWarehouseman) {
        base.status = 'WAITING_PURCHASE_PRODUCTS';
      }

      // Logika filtrów ze starszych popupów dashboardu
      if (this.popupFilter !== '') {
        if (this.popupFilter === 'WITHOUT_SETTLEMENT') {
          base.without_settlement = 1;
        } else if (this.popupFilter === 'COLLECTED_BY_WAREHOUSEMAN') {
          base.collected_by_warehouseman = 1;
        } else if (this.popupFilter === 'OFFERS_TO_CHECK') {
          base.last_contact = 7;
        } else {
          base.warning = this.popupFilter;
        }
      }

      if (this.popupStatus !== '') {
        if (this.popupStatus === 'BLOCKADE') {
          base.blockade_status = 'BLOCKADE';
        } else if (this.popupStatus === 'OFFERS_TO_CHECK') {
          base.status = ['NEW', 'SENT_TO_CUSTOMER', 'OFFER_CLOSED'];
        } else {
          base.status = this.popupStatus;
        }
      }

      if (this.popupFilter === 'OBTAINING') {
        base.blockade_status = '';
      }

      return base;
    }
  },
  data () {
    return {
      currencies: [],
      orders: [],
      workers: [],
      countries: [],
      contractors: [],
      productTags: [],
      customerTags: [],
      orderTags: [],
      promotionalTags: [],
      products: [],
      warehousemans: [],
      loaded: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadedWorkers: false,
      loadedContractors: false,
      loadedWarehousemans: false,
      loadedProductTags: false,
      loadedProducts: false,
      loadedCustomerTags: false,
      loadedOrderTags: false,
      loadedPromotionalTags: false,
      loadError: false,
      downloadingPdf: false,
      downloadingXls: false,
      downloadingSummary: false,
      downloadingAttachment: false,
      downloadingInvoice: false,
      removeInProgress: false,
      duplicateInProgress: false,
      takeOrderInProgress: false,
      skipLSPagination: false,
      confirmPurchaseProductsInProgress: false,
      showAllOrders: false,
      displayChangeStatusPopup: false,
      statusChangeOrderName: '',
      statusChangeOrderID: null,
      statusChangeOrderStatus: null,
      statusChangeInProgress: false,
      downloadInProgress: false,
      downloadingMarkingInProgress: false,
      reminderSendingInProgress: false,
      // order summary popup
      showOrderSummaryPopup: false,
      orderSummaryID: 0,
      // helpers
      lastViewedItem: {
        action: '',
        id: null
      },
      activeFilters: {},
      apiQueryPayload: null
    };
  },
  created () {
    if (this.inPopup) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    if (this.userIsWarehouseman) {
      this.retrieveLastViewedData();
    }

    this.loadAdditionalData();
    this.$bus.$on('order-summary-popup-open', this.openOrderSummaryPopup);
    this.$bus.$on('order-summary-popup-close', this.closeOrderSummaryPopup);
    this.$bus.$on('order-unblock-in-progress', this.loadFilteredDataOnUnblock);
  },
  methods: {
    handleFiltersInit (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;

      if (!this.$root.orderUnblockInProgress) {
        this.loadFilteredData();
      }
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;

      if (this.$root.orderUnblockInProgress) {
        return;
      }
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData (downloadAsFile = false) {
      let currentApiPayload = this.apiQueryPayload;
      if (downloadAsFile && this.$refs.listFilters) {
        currentApiPayload = this.$refs.listFilters.getApiPayload();
      }

      let whereData = {
        ...this.baseApiWhere,
        ...(cloneDeep(currentApiPayload) || {})
      };

      let allItemsParam = '';

      if (this.isAllWarehouseOrdersView) {
        whereData.status = [];
        whereData.excluded_status = ['DRAFT'];
        allItemsParam = '?all_items=1';
      }

      let requestConfig = {
        with: ['settlements', 'orderProducts', 'purchase', 'emails', 'complaints'],
        where: whereData
      };

      if (this.userIsWorker && (this.showAllOrders || this.activeFilters.show_all)) {
        requestConfig.show_all = 1;
      }

      if (downloadAsFile) {
        if (this.downloadInProgress) {
          return;
        }

        whereData['download_excel'] = 1;
        this.downloadInProgress = true;

        this.$http.post(this.getApiEndpoint('/orders/items' + allItemsParam), requestConfig, {
          responseType: 'blob'
        }).then((response) => {
          let link = document.createElement('a');
          link.href = URL.createObjectURL(new Blob([response.data]));
          link.setAttribute('download', this.$t('order.ordersXlsFilename') + '.xlsx');

          if (this.inPopup) {
            document.querySelector('.big-popup .modal-body').appendChild(link);
            link.click();
            document.querySelector('.big-popup .modal-body').removeChild(link);
          } else {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }

          this.downloadInProgress = false;
        }).catch(() => {
          this.downloadInProgress = false;
        });
      } else {
        ListUtils.loadPaginatedItemsData(this, {
          endpoint: this.getApiEndpoint('/orders/items?page=' + this.currentPage + allItemsParam.replace('?', '&')),
          listField: 'orders',
          loadError: 'loadError',
          method: 'get',
          params: requestConfig,
          successAction () {
            Vue.nextTick(() => {
              if (document.querySelector('tr.has-been-last-visited')) {
                document.querySelector('tr.has-been-last-visited').scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'center'
                });
              }
            });
          }
        });
      }
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      if (!this.isInquiriesView) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/products/all-items-id-code'),
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: +this.isIndentList
            }
          }
        });
      } else {
        this.loadedProducts = true;
      }

      if (!this.userIsCustomer && !this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/customers/tags/items',
          loadedKey: 'loadedCustomerTags',
          outputKey: 'customerTags',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });
      } else {
        this.loadedCustomerTags = true;
      }

      if (!this.userIsCustomer && !this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/orders/tags/items',
          loadedKey: 'loadedOrderTags',
          outputKey: 'orderTags',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });
      } else {
        this.loadedOrderTags = true;
      }

      if (!this.userIsCustomer && !this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/orders/promotional-tags/items',
          loadedKey: 'loadedPromotionalTags',
          outputKey: 'promotionalTags',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });
      } else {
        this.loadedPromotionalTags = true;
      }

      if (this.userIsWarehouseman) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWarehousemans',
          outputKey: 'warehousemans',
          errorKey: 'loadError',
          params: {
            where: {
              type: 'warehouseman'
            },
            return: 'get'
          }
        });
      } else {
        this.loadedWarehousemans = true;
      }

      if (!this.userIsCustomer) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/contractors/items'),
          loadedKey: 'loadedContractors',
          outputKey: 'contractors',
          errorKey: 'loadError',
          params: {
            return: 'get'
          }
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWorkers',
          params: {
            return: 'get',
            where: {
              type: ['worker', 'admin']
            }
          },
          outputKey: 'workers',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/tags/categories/items'),
          loadedKey: 'loadedProductTags',
          params: {
            with: ['tags']
          },
          outputKey: 'productTags',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/countries/items'),
          loadedKey: 'loadedCountries',
          outputKey: 'countries',
          errorKey: 'loadError'
        });
      } else {
        this.loadedCountries = true;
        this.loadedProductTags = true;
        this.loadedContractors = true;
        this.loadedWorkers = true;
      }
    },
    removeOrder (orderID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/orders/delete/'),
        id: orderID,
        successTitle: this.$t('order.orderDeleteSuccessTitle'),
        successText: this.$t('order.orderDeleteSuccessText'),
        confirmTitle: this.$t('order.orderDeleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderDeleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('sidebar-counters-refresh');
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    duplicateOrder (orderID) {
      this.duplicateInProgress = true;

      Vue.swal({
        title: this.$t('order.duplicatePopup.title'),
        html: this.$t('order.duplicatePopup.text'),
        type: 'question',
        input: 'checkbox',
        inputPlaceholder: this.$t('order.duplicatePopup.copyFiles'),
        inputValue: false,
        showCancelButton: true,
        confirmButtonText: this.$t('order.duplicatePopup.duplicateOrder'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value === 1 || result.value === 0) {
          let duplicateData = {};

          if (result.value === 1) {
            duplicateData = {
              duplicate_fields: ['files']
            };
          }

          this.$http.post(this.getApiEndpoint('/orders/duplicate/' + orderID), duplicateData).then(response => {
            if (response.data.success) {
              Vue.swal({
                title: this.$t('order.itemDuplicateSuccess'),
                html: '',
                type: 'success',
                timer: 2000,
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });

              this.loadFilteredData();
            } else {
              Vue.swal({
                title: this.$t('order.itemDuplicateError'),
                html: this.$t('ui.tryAgain'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }

            this.duplicateInProgress = false;
          }).catch(error => {
            console.log(error);
            Vue.swal({
              title: this.$t('order.itemDuplicateError'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.duplicateInProgress = false;
          });
        } else {
          this.duplicateInProgress = false;
        }
      });
    },
    // eslint-disable-next-line
    downloadPdf (orderID, orderName) {
      this.downloadingPdf = orderID;

      this.$http.post(this.getApiEndpoint('/orders/offer/' + orderID), {
        type: 'pdf'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        filename = filename.replace(/"/gmi, '');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPdf = false;
      });
    },
    downloadXls (orderID, orderStatus) {
      this.downloadingXls = orderID;

      this.$http.post(this.getApiEndpoint('/orders/offer/' + orderID), {
        type: 'xlsx'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('order.orderFileNamePrefix');

        if (orderStatusPhase['offer-file-prefix'].indexOf(orderStatus) > -1) {
          filenamePrefix = this.$t('order.offerFileNamePrefix');
        }

        link.setAttribute('download', filenamePrefix + orderID + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingXls = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingXls = false;
      });
    },
    // eslint-disable-next-line
    downloadSummary (orderID, orderName) {
      this.downloadingSummary = orderID;

      this.$http.post(this.getApiEndpoint('/orders/summary/' + orderID), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        filename = filename.replace(/"/gmi, '');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingSummary = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingSummary = false;
      });
    },
    downloadInvoice (orderData) {
      this.downloadingInvoice = orderData.id;
      let endpoint = orderData.settlements[0].file_url;
      // let fileName = orderData.settlements[0].number + '_' + orderData.order_name + '.pdf';

      this.$http.post(endpoint, {}, {
        responseType: 'blob'
      }).then(response => {
        let fileName = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        fileName = fileName.replace(/[\s\/\\]/gmi, '-', fileName);

        if (orderData.settlements.length > 1) {
          endpoint = orderData.settlements_zipped_files;
          fileName = 'faktury-do-zamowienia-' + orderData.id + '.zip';
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingInvoice = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingInvoice = false;
      });
    },
    downloadAttachment (orderID) {
      this.downloadingAttachment = orderID;

      this.$http.post(this.getApiEndpoint('/orders/download-order-attachment/' + orderID), {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingAttachment = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingAttachment = false;
      });
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    getPromotionalTag (order) {
      if (!order.promotional_tag_id) {
        return null;
      }

      return this.promotionalTags.find(tag => tag.id === order.promotional_tag_id) || null;
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    getStatusName (status, orderIsCollectedBy) {
      let statusName = ListUtils.getKeyLabel(orderStatuses[this.$i18n.locale], status);

      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return this.$t('order.beeingCollected');
      }

      if (status === 'SENT_TO_CUSTOMER' && this.userIsCustomer) {
        return this.$t('order.forAcceptance');
      }

      return statusName;
    },
    getStatusClass (status) {
      return ListUtils.getKeyLabel(orderStatusClasses, status).replace(' outline', '');
    },
    getOutline (status, orderIsCollectedBy) {
      if (status === 'TO_COLLECT' && orderIsCollectedBy === this.getUserID) {
        return false;
      }

      return ListUtils.getKeyLabel(orderStatusClasses, status).indexOf(' outline') > -1;
    },
    getServicesCount (products, services) {
      let productsCount = products.map(product => product.services.length).reduce((a, b) => a + b, 0);
      let servicesCount = 0;

      if (services && services.length > 0) {
        servicesCount = services.length;
      }

      return productsCount + servicesCount;
    },
    hasDeadlineWarning (order) {
      if (order.warnings && order.warnings.filter(warning => warning.type === 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    hasShippingDateWarning (order) {
      if (order.shipping_date_warning) {
        return !!order.shipping_date_warning;
      }

      return false;
    },
    hasBlockadeDateWarning (order) {
      if (order.blockade_status === 'BLOCKADE' && order.blocked_at) {
        let blockadeDate = new Date(order.blocked_at).getTime();
        let currentDate = new Date().getTime();

        if (currentDate - blockadeDate > 7 * 24 * 60 * 60 * 1000) {
          return true;
        }
      }

      return false;
    },
    hasServicesWarnings (order) {
      if (order.warnings && order.warnings.filter(warning => warning.type !== 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    hasUnpaidAdvance (order) {
      return this.userIsAdminOrWorker && order.is_advance && !order.advance_paid;
    },
    takeOrder (orderID) {
      this.takeOrderInProgress = orderID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/orders/take-to-collect/' + orderID),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderCollectErrorText'),
        successAction: () => {
          this.takeOrderInProgress = false;

          if (this.isIndentList) {
            this.$router.push('/zamowienia-indent/edycja/' + parseInt(orderID, 10));
          } else {
            this.$router.push('/zamowienia/edycja/' + parseInt(orderID, 10));
          }
        },
        failAction: () => {
          this.takeOrderInProgress = false;
        }
      });
    },
    orderActionUrl (orderID, isIndent, newWindow = false) {
      if (this.isAllWarehouseOrdersView) {
        return this.$router.push('/zamowienia-wszystkie/podglad/' + orderID);
      }

      if (!newWindow) {
        if (this.isInquiriesView) {
          return this.$router.push('/zapytania/zapytanie/' + orderID);
        }

        if (this.isOffersView) {
          if (this.isIndentList) {
            return this.$router.push('/oferty-indent/edycja/' + orderID);
          }

          return this.$router.push('/oferty/edycja/' + orderID);
        }

        if (isIndent) {
          return this.$router.push('/zamowienia-indent/edycja/' + orderID);
        }

        return this.$router.push('/zamowienia/edycja/' + orderID);
      }

      let routeData = this.$router.resolve('/zamowienia/edycja/' + orderID);

      if (this.isIndentList) {
        routeData = this.$router.resolve('/zamowienia-indent/edycja/' + orderID);
      }

      if (this.isInquiriesView) {
        routeData = this.$router.resolve('/zapytania/zapytanie/' + orderID);
      }

      if (this.isOffersView) {
        if (this.isIndentList) {
          routeData = this.$router.resolve('/oferty-indent/edycja/' + orderID);
        } else {
          routeData = this.$router.resolve('/oferty/edycja/' + orderID);
        }
      }

      window.open(routeData.href, '_blank');
      return false;
    },
    filterByProductCodes (option, label, search) {
      return (option.valueLowerCase).indexOf(search) > -1;
    },
    filteredProductCodes (options, search) {
      let searchLowerCase = search.toLowerCase();
      return options.filter(option => this.filterByProductCodes(option, option.id, searchLowerCase)).slice(0, 50);
    },
    showOrderStatusPopup (orderName, orderID, orderStatus) {
      if (
        (orderStatus === 'ENDED' && !this.userIsAdmin) ||
        orderStatus === 'DELETED' ||
        orderStatus === 'TO_DELETE'
      ) {
        Vue.swal({
          title: this.$t('status.operationNotPermitted'),
          html: this.$t('status.operationNotPermittedDesc'),
          type: 'error',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.statusChangeOrderName = orderName;
      this.statusChangeOrderID = orderID;
      this.statusChangeOrderStatus = orderStatus;
      this.statusChangeInProgress = false;

      Vue.nextTick(() => {
        this.displayChangeStatusPopup = true;
      });
    },
    saveNewOrderStatus () {
      let selectedOrder = this.orders.filter(order => order.id === this.statusChangeOrderID);

      if (
        selectedOrder &&
        selectedOrder[0] &&
        !selectedOrder[0].settlements.length &&
        this.statusChangeOrderStatus === 'ENDED'
      ) {
        Vue.swal({
          title: this.$t('order.noSettlementsTitle'),
          text: this.$t('order.noSettlementsText'),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('status.yesChangeStatus'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            FormUtils.submit({
              $http: this.$http,
              endpoint: '/orders/statuses/change-status/' + this.statusChangeOrderID,
              formData: {
                status: this.statusChangeOrderStatus
              },
              successTitle: this.$t('status.changeStatusSuccessTitle'),
              successText: this.$t('status.changeStatusSuccessText'),
              errorTitle: this.$t('ui.errorOccured'),
              errorText: this.$t('status.changeStatusErrorText'),
              successAction: () => {
                this.statusChangeInProgress = false;
                this.displayChangeStatusPopup = false;
                this.debouncedLoadFilteredData();
              },
              failAction: () => {
                this.statusChangeInProgress = false;
              }
            });
          } else {
            this.statusChangeInProgress = false;
            this.displayChangeStatusPopup = false;
          }
        });
      } else {
        FormUtils.submit({
          $http: this.$http,
          endpoint: '/orders/statuses/change-status/' + this.statusChangeOrderID,
          formData: {
            status: this.statusChangeOrderStatus
          },
          successTitle: this.$t('status.changeStatusSuccessTitle'),
          successText: this.$t('status.changeStatusSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('status.changeStatusErrorText'),
          successAction: () => {
            this.statusChangeInProgress = false;
            this.displayChangeStatusPopup = false;
            this.debouncedLoadFilteredData();
          },
          failAction: () => {
            this.statusChangeInProgress = false;
          }
        });
      }
    },
    sendReminder (orderID, type) {
      this.reminderSendingInProgress = orderID;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/emails/send/' + orderID,
        formData: {
          type
        },
        successTitle: this.$t('order.reminderSentTitle'),
        successText: this.$t('order.reminderSentDesc'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.reminderErrorTitle'),
        successAction: () => {
          this.reminderSendingInProgress = false;
          this.debouncedLoadFilteredData();
        },
        failAction: () => {
          this.reminderSendingInProgress = false;
        }
      });
    },
    openOrderSummaryPopup (orderID) {
      this.showOrderSummaryPopup = true;
      this.orderSummaryID = orderID;
    },
    closeOrderSummaryPopup () {
      this.showOrderSummaryPopup = false;
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => (n < 10 ? '0' + n : n)).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => (n < 10 ? '0' + n : n)).join('-');
    },
    getPurchaseStatus (status) {
      switch (status) {
        case 'NEW': return this.$t('purchase.newOrder');
        case 'WAITING_PRODUCTS': return this.$t('order.waitingPurchaseProducts');
        case 'WAITING_PAYMENT': return this.$t('order.pendingPayment');
        case 'CLOSED': return this.$t('purchase.closed');
        default: return '';
      }
    },
    loadFilteredDataOnUnblock (state) {
      if (!state) {
        this.loadFilteredData();
      }
    },
    confirmProductsReceive (orderID) {
      this.confirmPurchaseProductsInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/receive-purchase-products/' + orderID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderPurchaseProductsConfirmErrorText'),
        successAction: (response) => {
          this.confirmPurchaseProductsInProgress = false;

          if (response.success) {
            if (this.isIndentList) {
              this.$router.push('/zamowienia-indent/edycja/' + orderID);
            } else {
              this.$router.push('/zamowienia/edycja/' + orderID);
            }
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('order.orderPurchaseProductsConfirmErrorText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.confirmPurchaseProductsInProgress = false;
        }
      });
    },
    complaintsStatus (complaints) {
      if (!complaints.length) {
        return false;
      }

      if (complaints.filter(complaint => complaint.status === 'IN_REALIZATION').length) {
        return 'IN_REALIZATION';
      }

      return 'ENDED';
    },
    downloadMarkingDoc (orderID) {
      this.downloadingMarkingInProgress = orderID;

      this.$http.post('/orders/box-marking/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('order.markingFileNamePrefix') + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingMarkingInProgress = false;

        if (this.isIndentList) {
          this.$router.push('/zamowienia-indent/edycja/' + parseInt(orderID, 10));
        } else {
          this.$router.push('/zamowienia/edycja/' + parseInt(orderID, 10));
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingMarkingInProgress = false;
      });
    },
    retrieveLastViewedData () {
      if (!localStorage.getItem('headwear-crm-warehouse-last-viewed')) {
        return;
      }

      let data = localStorage.getItem('headwear-crm-warehouse-last-viewed').split(';');

      if (data[0] && data[1] && data[2] && parseInt(data[2], 10) > +new Date() - (1 * 60 * 1000)) {
        this.lastViewedItem = {
          action: data[0],
          id: parseInt(data[1], 10)
        };
      }

      localStorage.removeItem('headwear-crm-warehouse-last-viewed');
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-summary-popup-open', this.openOrderSummaryPopup);
    this.$bus.$off('order-summary-popup-close', this.closeOrderSummaryPopup);
    this.$bus.$off('order-unblock-in-progress', this.loadFilteredDataOnUnblock);
  }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

tr.has-deadline {
  td:first-child {
    border-left: 8px solid #c4183c;
  }
}

tr.has-been-last-visited {
  td {
    border-top: 8px solid #ffb400;
  }
}

tr.has-shipping-warning {
  td {
    background: rgba(196, 24, 60, .1);
  }
}

tr.has-blockade-warning {
  td {
    background: rgba(255, 180, 0, .15);
  }
}

tr.has-unpaid-advance {
  td {
    background: #f22;
    color: #fff;

    & /deep/ span[class*="badge-outline"] {
      background: #fff;
    }

    & /deep/ button[class*="btn-outline"] {
      background: #fff;
    }

    & /deep/ .text-danger {
      color: #fff!important;
    }
  }
}

.customer-info {
  position: relative;

  .is-inquiry-info {
    display: inline-block;
    font-size: 10px;
    text-transform: uppercase;

    .material-icons {
      position: relative;
      top: 3px;
    }
  }
}

.purchase-info {
  border: 1px solid #ccc;
  border-radius: 5px;
  margin: 1rem 0 0 0;
  padding: .5rem;

  &-content {
    span {
      display: block;
      font-size: 12px;
      font-weight: normal;
    }
  }
}

.br-to-hide:first-child {
  display: none;
}
</style>
